import { ref, reactive, computed, watch, onMounted, nextTick, onUnmounted, openBlock, createElementBlock, normalizeClass, normalizeStyle, createElementVNode, renderSlot } from 'vue';
import propTypes from './utils/prop-types';
import { CHANGE_EVENT, SCROLL_EVENT } from './utils/constants';
import { createComponent } from './utils/create-component';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var props = {
  /**
   * 粘性布局的位置
   */
  position: propTypes.string().def('top'),
  /**
   * fixed时在对应position的偏移
   */
  offset: propTypes.number().def(0),
  /**
   * fixed后的z-index
   */
  zIndex: propTypes.number().def(10),
  /**
   * 指定限制容器
   */
  container: propTypes.dom(),
  /**
   * 是否激活sticky
   */
  activated: propTypes["boolean"]().def(true)
};
var emits = [CHANGE_EVENT, SCROLL_EVENT];

var _createComponent = createComponent('sticky'),
  create = _createComponent.create,
  className = _createComponent.className;
var script = create({
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var emit = _ref.emit;
    var shadowRef = ref();
    var state = reactive({
      fixed: false,
      // 宽高
      width: 0,
      height: 0,
      // 如果有container时，通过translateY限制Sticky位置
      translateY: 0
    });
    // shadow节点样式，在fixed触发时，占位
    var shadowStyle = computed(function () {
      if (!state.fixed) return undefined;
      return {
        width: "".concat(state.width, "px"),
        height: "".concat(state.height, "px")
      };
    });
    // fixed
    var contentStyle = computed(function () {
      var _ref2;
      if (!state.fixed) return undefined;
      return _ref2 = {
        zIndex: props.zIndex,
        width: "".concat(state.width, "px"),
        height: "".concat(state.height, "px")
      }, _defineProperty(_ref2, props.position, "".concat(props.offset, "px")), _defineProperty(_ref2, "transform", "translateY(".concat(state.translateY, "px)")), _ref2;
    });
    var contentClassNames = computed(function () {
      return ["".concat(className, "__content"), _defineProperty({}, "".concat(className, "__content-fixed"), state.fixed)];
    });
    function getContainerRect() {
      // 如果没有指定容器，容器就是浏览器窗口
      if (!props.container || shadowRef.value && !props.container.contains(shadowRef.value)) {
        return {
          top: 0,
          bottom: window.innerHeight
        };
      }
      var rect = props.container.getBoundingClientRect();
      return {
        top: rect.top,
        bottom: rect.bottom
      };
    }
    function updateStatus() {
      /* istanbul ignore next */
      if (!shadowRef.value) {
        state.fixed = false;
        return {
          top: 0
        };
      }
      var rect = shadowRef.value.getBoundingClientRect();
      // 如果元素不可见，不用执行sticky逻辑
      if (rect.width === 0 || rect.height === 0 || !props.activated) {
        state.fixed = false;
        return rect;
      }
      var containerRect = getContainerRect();
      // top和bottom分别处理
      if (props.position === 'bottom') {
        // 距离底部的距离小于offset
        state.fixed = window.innerHeight - rect.bottom < props.offset;
        // 容器顶部和Sticky顶部距离 = 容器顶部距窗口顶部距离 - Sticky顶部距窗口顶部距离
        var topDistance = containerRect.top - (window.innerHeight - rect.height - props.offset);
        // 如果 topDistance 大于0，认为超出了容器范围，通过translateY回正
        if (topDistance > 0) {
          state.translateY = topDistance;
        } else {
          state.translateY = 0;
        }
      } else {
        // 距离顶部的距离小于offset
        state.fixed = rect.top < props.offset;
        // 容器底部和Sticky底部距离 = 容器底部距窗口顶部距离 - Sticky底部距窗口顶部距离
        var bottomDistance = containerRect.bottom - (rect.height + props.offset);
        // 如果 bottomDistance 小于0，认为超出了容器范围，通过translateY回正
        if (bottomDistance < 0) {
          state.translateY = bottomDistance;
        } else {
          state.translateY = 0;
        }
      }
      // 设置fixed时的宽高
      if (state.fixed) {
        state.width = rect.width;
        state.height = rect.height;
      }
      return rect;
    }
    function onScroll() {
      var rect = updateStatus();
      emit(SCROLL_EVENT, {
        // 原始位置的top值
        top: rect.top,
        fixed: state.fixed
      });
    }
    watch(function () {
      return props.activated;
    }, function () {
      updateStatus();
    });
    watch(function () {
      return state.fixed;
    }, function (val) {
      emit(CHANGE_EVENT, val);
    });
    onMounted(function () {
      window.addEventListener('scroll', onScroll);
      // 有可能mounted之后才能获取到container
      nextTick(function () {
        updateStatus();
      });
    });
    onUnmounted(function () {
      window.removeEventListener('scroll', onScroll);
    });
    return {
      className: className,
      shadowRef: shadowRef,
      shadowStyle: shadowStyle,
      contentStyle: contentStyle,
      contentClassNames: contentClassNames
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    ref: "shadowRef",
    "class": normalizeClass(_ctx.className),
    style: normalizeStyle(_ctx.shadowStyle)
  }, [createElementVNode("div", {
    "class": normalizeClass(_ctx.contentClassNames),
    style: normalizeStyle(_ctx.contentStyle)
  }, [renderSlot(_ctx.$slots, "default")], 6 /* CLASS, STYLE */)], 6 /* CLASS, STYLE */);
}

script.render = render;
script.__file = "components/src/component/sticky/sticky.vue";

var _Sticky = script;

export { _Sticky as default };
